import { z } from 'zod';
export const documentsTemplatesCapability = 'documents.templates.v1';
export const documentsTemplatePreviewCapability = 'documents.template-preview.v1';
export const documentsTemplateIdSchema = z.string().uuid();
export const documentsTemplateVersionIdSchema = z.string().uuid();
export const documentsTemplateCommandIdSchema = z.string().uuid();
export const documentsTemplateKeySchema = z.string().trim().min(1).max(100).regex(/^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/, 'Document template keys use lower-case letters, numbers, hyphens, and underscores.');
export const documentsTemplateCategorySchema = z.string().trim().min(1).max(80).regex(/^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/, 'Document template categories use lower-case letters, numbers, hyphens, and underscores.');
export const documentsTemplatePurposeSchema = z.enum([
    'contract',
    'notice',
    'letter',
    'form',
    'certificate',
    'other',
]);
export const documentsTemplateStatusSchema = z.enum(['draft', 'published', 'archived']);
export const documentsTemplateSensitivitySchema = z.enum(['ordinary', 'confidential', 'restricted']);
export const documentsTemplatePageFormatSchema = z.enum(['letter', 'legal', 'a4']);
export const documentsTemplateOrientationSchema = z.enum(['portrait', 'landscape']);
export const documentsTemplateMarginPresetSchema = z.enum(['compact', 'standard', 'wide']);
export const documentsTemplateVariableTypeSchema = z.enum([
    'text',
    'multiline',
    'date',
    'number',
    'currency',
    'boolean',
]);
export const documentsTemplateVariableKeySchema = z.string().trim().min(1).max(100).regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/, 'Document variable keys use lower-case snake case.');
export const documentsTemplateVariableDefinitionSchema = z.strictObject({
    key: documentsTemplateVariableKeySchema,
    label: z.string().trim().min(1).max(120),
    type: documentsTemplateVariableTypeSchema.default('text'),
    required: z.boolean().default(false),
    placeholder: z.string().trim().max(160).nullable().default(null),
    helpText: z.string().trim().max(500).nullable().default(null),
    maximumLength: z.number().int().min(1).max(10_000).nullable().default(null),
});
export const documentsTemplatePageSchema = z.strictObject({
    format: documentsTemplatePageFormatSchema.default('letter'),
    orientation: documentsTemplateOrientationSchema.default('portrait'),
    marginPreset: documentsTemplateMarginPresetSchema.default('standard'),
});
export const documentsTemplateContentSchema = z.strictObject({
    bodyHtml: z.string().trim().min(1).max(500_000),
    variables: z.array(documentsTemplateVariableDefinitionSchema).max(100).default([]),
    requiresSignature: z.boolean().default(false),
    consentText: z.string().trim().min(1).max(4_000).nullable().default(null),
    sensitivity: documentsTemplateSensitivitySchema.default('ordinary'),
    page: documentsTemplatePageSchema.default({
        format: 'letter', orientation: 'portrait', marginPreset: 'standard',
    }),
});
export const documentsTemplateVersionSchema = documentsTemplateContentSchema.extend({
    id: documentsTemplateVersionIdSchema,
    tenantId: z.string().uuid(),
    templateId: documentsTemplateIdSchema,
    versionNumber: z.number().int().positive(),
    sourceVersionId: documentsTemplateVersionIdSchema.nullable(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(5).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const documentsTemplateSchema = z.strictObject({
    id: documentsTemplateIdSchema,
    tenantId: z.string().uuid(),
    key: documentsTemplateKeySchema,
    name: z.string().trim().min(1).max(180),
    category: documentsTemplateCategorySchema,
    purpose: documentsTemplatePurposeSchema,
    status: documentsTemplateStatusSchema,
    publishedVersionId: documentsTemplateVersionIdSchema.nullable(),
    latestVersionId: documentsTemplateVersionIdSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    publishedVersion: documentsTemplateVersionSchema.nullable(),
    latestVersion: documentsTemplateVersionSchema,
});
export const documentsTemplateListQuerySchema = z.strictObject({
    includeArchived: z.boolean().default(false),
    category: documentsTemplateCategorySchema.nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
});
const documentsTemplateIdentityInputSchema = z.strictObject({
    key: documentsTemplateKeySchema,
    name: z.string().trim().min(1).max(180),
    category: documentsTemplateCategorySchema,
    purpose: documentsTemplatePurposeSchema,
});
export const documentsCreateTemplateSchema = documentsTemplateIdentityInputSchema.extend({
    commandId: documentsTemplateCommandIdSchema,
    content: documentsTemplateContentSchema,
    reason: z.string().trim().min(5).max(500),
});
export const documentsAppendTemplateVersionSchema = z.strictObject({
    commandId: documentsTemplateCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    content: documentsTemplateContentSchema,
    reason: z.string().trim().min(5).max(500),
});
export const documentsPublishTemplateVersionSchema = z.strictObject({
    commandId: documentsTemplateCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    versionId: documentsTemplateVersionIdSchema,
    reason: z.string().trim().min(10).max(500),
});
export const documentsTemplateStateCommandSchema = z.strictObject({
    commandId: documentsTemplateCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const documentsRollbackTemplateSchema = documentsTemplateStateCommandSchema.extend({
    sourceVersionId: documentsTemplateVersionIdSchema,
});
export const documentsTemplateVariableValueSchema = z.union([
    z.string().max(10_000),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);
export const documentsTemplatePreviewRequestSchema = z.strictObject({
    content: documentsTemplateContentSchema,
    values: z.record(documentsTemplateVariableKeySchema, documentsTemplateVariableValueSchema).default({}),
});
export const documentsTemplatePreviewResponseSchema = z.strictObject({
    data: z.strictObject({
        html: z.string(),
        contentHash: z.string().regex(/^[a-f0-9]{64}$/),
        renderedHtmlHash: z.string().regex(/^[a-f0-9]{64}$/),
        usedVariableKeys: z.array(documentsTemplateVariableKeySchema),
        missingRequiredVariables: z.array(documentsTemplateVariableKeySchema),
        requiresSignature: z.boolean(),
        sensitivity: documentsTemplateSensitivitySchema,
        page: documentsTemplatePageSchema,
    }),
});
const documentsCommandMetadataSchema = z.strictObject({
    commandId: documentsTemplateCommandIdSchema,
    idempotent: z.boolean(),
});
export const documentsTemplateResponseSchema = z.strictObject({
    data: documentsTemplateSchema,
    command: documentsCommandMetadataSchema.optional(),
});
export const documentsTemplatesResponseSchema = z.strictObject({
    data: z.array(documentsTemplateSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const documentsTemplateVersionsResponseSchema = z.strictObject({
    data: z.array(documentsTemplateVersionSchema),
});
//# sourceMappingURL=documents-template.contract.js.map